import { pick } from 'lodash';
import { Selector } from '@ngxs/store';
import { CommentSectionItem, MessageEntityType } from '../comments.model';
import { CommentsState } from './comments.state';
import { CommentsStateModel } from './comments-state.model';
import { ClientRequestsState } from '../../../state/client-requests.state';
import { ClientRequestItemActivityViewModel } from '../../../models';
import { hasUnreadMessages, mergeCommentsWithActivity } from '../comments.utils';
import { ClientRequestModel, RequestDocument } from '@aiwyn/api';
import { CommonClientRequestsState } from '@aiwyn/common/client-requests';

/**
 * NGXS Selectors for Comments State
 */
export class CommentsSelectors {
  /**
   * Select all comments state
   */
  @Selector([CommentsState])
  static state(state: CommentsStateModel): CommentsStateModel {
    return state;
  }

  /**
   * Signal of Unread Comments present
   */
  @Selector([CommentsState])
  static hasUnreadComments(state: CommentsStateModel): boolean {
    return Object.values(state.commentsByEntity).some((comments) => {
      return comments.filter((comment) => hasUnreadMessages(comment)).length > 0;
    });
  }

  @Selector([
    CommentsState,
    ClientRequestsState.getItemActivityViewModels,
    ClientRequestsState.getSelectedClientRequest,
    CommonClientRequestsState.getAllRestrictedItems,
  ])
  static getCommentsPlusActivity(
    state: CommentsStateModel,
    activity: ClientRequestItemActivityViewModel[],
    selectedRequest: ClientRequestModel,
    restrictedItems: RequestDocument[],
  ): CommentsStateModel<CommentSectionItem> {
    return {
      ...pick(state, ['loading', 'error']),
      commentsByEntity: mergeCommentsWithActivity(state, activity, selectedRequest, restrictedItems),
    };
  }

  /**
   * Select loading state
   */
  @Selector()
  static loading(state: CommentsStateModel): boolean {
    return state.loading;
  }

  /**
   * Select error state
   */
  @Selector()
  static error(state: CommentsStateModel): string | null {
    return state.error;
  }

  // ========================================================================
  // =========================== HELPER FUNCTIONS ===========================
  // ========================================================================

  /**
   * Select count of unread comments for a specific entity (including replies)
   * A comment is considered unread if it or any of its replies are unread
   *
   * @param state - State Snapshot
   * @param entityType - The type of entity (CLIENT_REQUEST_ITEM, ENGAGEMENT, JOB)
   * @param entityId - The ID of the entity
   * @returns A selector that returns the unread count for the specified entity
   */
  static unreadCountByEntity(
    state: CommentsStateModel,
    entityId: number,
    entityType: MessageEntityType = MessageEntityType.CLIENT_REQUEST_ITEM,
  ): number {
    const key = `${entityType}:${entityId}`;
    const comments = state.commentsByEntity[key] || [];
    // Count top-level comments that have unread messages (including in their reply tree)
    return comments.filter((comment) => hasUnreadMessages(comment)).length;
  }

  /**
   * Select comments for a specific entity
   */
  static commentsByEntity(
    state: CommentsStateModel<CommentSectionItem>,
    entityIds: number[],
    entityType: MessageEntityType = MessageEntityType.CLIENT_REQUEST_ITEM,
  ): CommentSectionItem[] {
    return entityIds.flatMap((entityId) => state.commentsByEntity[`${entityType}:${entityId}`] || []);
  }
}
